import { selectIsCampaignAlertOverridesFeatureFlagOn } from 'core/userContext';
import { FC, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { CollapsibleContent, CollapsibleContentArrow } from 'ui/collapsibleContent';
import { Bell16, DisableAlert } from 'ui/icons';
import { borderRadius, button, caption, colors, spacing } from 'ui/theme';
import { pluralizeWord } from '../../utils';
import { CampaignOverridesTrigger } from './CampaignOverridesTrigger';

const Card = styled.div<{ $allowsOverride?: boolean }>`
  background: ${colors.backgroundSecondary};
  padding: ${spacing.base};
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const HeaderText = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  ${button};
  cursor: pointer;
`;

const PaddedHeader = styled(Header)`
  padding: ${spacing.base};
`;

const List = styled.ul`
  list-style-type: disc;
  ${caption}
  color: ${colors.textSecondary};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: ${spacing.threeQuarter} 0 0 0;
`;

export const CAMPAIGN_ALERT_DETAILS_ID = 'campaign-alert-details';
export const CAMPAIGN_ALERT_DETAILS_HEADER_ID = 'header';
export const DISABLED_ALERTS_ID = 'disabled-alerts';

export const DisabledAlerts = () => (
  <PaddedHeader data-test={DISABLED_ALERTS_ID}>
    <HeaderText>
      <DisableAlert />
      Alerts Disabled
    </HeaderText>
  </PaddedHeader>
);

type Props = {
  details: string[];
  isNewCampaign?: boolean;
  onTakeoverOpen: (isOpen: boolean) => void;
};

export const CampaignAlertDetails: FC<Props> = ({
  details,
  isNewCampaign = false,
  onTakeoverOpen,
  ...rest
}) => {
  const alertCount = details?.length || 0;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const handleClick = useCallback(() => setIsCollapsed((collapsed) => !collapsed), []);
  const isCampaignAlertOverridesEnabled = useSelector(selectIsCampaignAlertOverridesFeatureFlagOn);
  return (
    <Card
      data-test={CAMPAIGN_ALERT_DETAILS_ID}
      {...rest}
      $allowsOverride={isCampaignAlertOverridesEnabled}
    >
      <Header>
        <HeaderText data-test={CAMPAIGN_ALERT_DETAILS_HEADER_ID} onClick={handleClick}>
          <Bell16 color={colors.iconPrimary} />
          {alertCount} {pluralizeWord('Alert', alertCount)} Enabled
          <CollapsibleContentArrow isCollapsed={isCollapsed} height={16} width={16} />
        </HeaderText>
        {isCampaignAlertOverridesEnabled && (
          <CampaignOverridesTrigger isDisabled={isNewCampaign} onTakeoverOpen={onTakeoverOpen} />
        )}
      </Header>
      <CollapsibleContent isCollapsed={isCollapsed}>
        <List>
          {details?.map((detail: string, idx: number) => (
            <li key={`${idx}`}>{detail}</li>
          ))}
        </List>
      </CollapsibleContent>
    </Card>
  );
};

export const CampaignEditorAlertDetails = styled(CampaignAlertDetails)`
  border-radius: ${borderRadius.xl};
`;
